'use client';

import { useQuery } from '@apollo/client';
import Colors from '@constants/Colors';
import { GET_DETAIL_ORGANIZATION_USER_PRO } from '@gql/organization-users';
import { Avatar, Breadcrumbs, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { Roles } from '@utils/roleUtils';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';


export const OrganizationUserHeader = ({
    userId,
    proId,
}: {
    proId: string;
    userId: string;
}) => {
    const theme = useTheme();
    const { data } = useQuery(GET_DETAIL_ORGANIZATION_USER_PRO, {
        variables: {
            id: userId,
            proId: proId
        },
    });

    const higherUpId = useMemo(() => {
        if (proId === data?.getOrganizationUser?.higherUpId) {
            return null;
        }
        return data?.getOrganizationUser?.higherUpId;
    }, [data, proId]);

    // Build ordered ancestor chain from root to direct parent
    const orderedHigherUps = useMemo(() => {
        const higherUps: any[] = data?.getOrganizationUser?.higherUps ?? [];
        const currentHigherUpId = data?.getOrganizationUser?.higherUpId;

        if (!higherUps.length || !currentHigherUpId) return [];

        const userMap = new Map(higherUps.map((u: any) => [u._id, u]));

        const chain: any[] = [];
        let currentId: string | null = currentHigherUpId;
        const visited = new Set<string>();

        while (currentId && userMap.has(currentId) && !visited.has(currentId)) {
            visited.add(currentId);
            const user = userMap.get(currentId);
            chain.push(user);
            currentId = user.higherUpId ?? null;
        }

        return chain.reverse();
    }, [data]);

    return (
        <Stack
            width="100%"
            gap={1}
            sx={{
                position: 'relative',
            }}>
            <Stack direction="row" gap={1} alignItems="center" style={{ position: 'relative' }}>
                <IconButton
                    size="small"
                    component={Link}
                    to={`/companies/${proId}/users${higherUpId ? `/${higherUpId}` : ''}`}>
                    <i className="fi fi-br-arrow-small-left"  />
                </IconButton>
                {orderedHigherUps.length > 0 && (
                    <Breadcrumbs aria-label="user hierarchy breadcrumb">
                        <Link
                            to={`/companies/${proId}/users`}
                            style={{ color: theme.palette.neutralGray.main, textDecoration: 'none', fontSize: '13px' }}>
                            Collaborateurs
                        </Link>
                        {orderedHigherUps.map((ancestor: any) => (
                            <Link
                                key={ancestor._id}
                                to={`/companies/${proId}/users/${ancestor._id}`}
                                style={{ color: theme.palette.neutralGray.main, textDecoration: 'none', fontSize: '13px' }}>
                                {ancestor.firstName} {ancestor.lastName}
                            </Link>
                        ))}
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.neutralWhite.main }}>
                            {data?.getOrganizationUser?.firstName} {data?.getOrganizationUser?.lastName}
                        </Typography>
                    </Breadcrumbs>
                )}
            </Stack>
            <Stack
                direction="row"
                alignItems="center"
                gap={2}
                sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 3,
                    p: 2,
                    mb: 1,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                    <Avatar
                        src={data?.getOrganizationUser?.file?.url}
                        sx={{
                            width: 56,
                            height: 56,
                            fontSize: 24,
                            bgcolor: Colors.PRIMARY_PURPLE_50,
                            fontWeight: 600,
                        }}>
                        {data?.getOrganizationUser?.firstName?.charAt(0)?.toUpperCase()}
                        {data?.getOrganizationUser?.lastName?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Stack gap={0.5} flex={1}>
                    <Typography
                        style={{
                            fontSize: '20px',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: 'normal',
                        }}>
                        {data?.getOrganizationUser?.firstName} {data?.getOrganizationUser?.lastName}
                    </Typography>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}>
                            {data?.getOrganizationUser?.email}
                        </Typography>
                        <Chip
                            size="small"
                            label={data?.getOrganizationUser?.role === Roles.ADMIN_PRO ? 'Admin' : 'Membre'}
                            color={data?.getOrganizationUser?.role === Roles.ADMIN_PRO ? 'primary' : 'secondary'}
                        />
                    </Stack>
                    </Stack>
                    <IconButton
                        size="small"
                        component={Link}
                        to={`/companies/${proId}/users/${userId}/edit`}>
                        <i className="fi fi-rr-pencil" style={{ display: 'flex' }} />
                    </IconButton>
            </Stack>
        </Stack>
    );
};

export default OrganizationUserHeader;
